/**
 * Service layer for UI Toast.
 * This service centralizes the state, orchestration, or data access used by its feature area.
 */
import { Injectable, signal } from '@angular/core';

import { getApiErrorMessage } from '@shared/utils';
import { UiToastState, UiToastType } from './ui-toast.types';

@Injectable({ providedIn: 'root' })
export class UiToastService {
  readonly toast = signal<UiToastState | null>(null);

  show(type: UiToastType, title: string, message: string, durationMs = 3000): void {
    this.toast.set({ type, title, message, durationMs });
  }

  success(title: string, message: string, durationMs?: number): void {
    this.show('success', title, message, durationMs);
  }

  info(title: string, message: string, durationMs?: number): void {
    this.show('info', title, message, durationMs);
  }

  warning(title: string, message: string, durationMs?: number): void {
    this.show('warning', title, message, durationMs);
  }

  error(title: string, message: string, durationMs?: number): void {
    this.show('error', title, message, durationMs);
  }

  apiSuccess(title = 'Success', message = 'Your changes were saved successfully.', durationMs?: number): void {
    this.success(title, message, durationMs);
  }

  apiFailure(
    title = 'Error',
    err?: unknown,
    fallback = 'Something went wrong. Please try again.',
    durationMs?: number
  ): void {
    this.error(title, getApiErrorMessage(err, fallback), durationMs);
  }

  validationWarning(title = 'Validation warning', message = 'Please complete the required fields before continuing.', durationMs?: number): void {
    this.warning(title, message, durationMs);
  }

  created(entityName: string, message?: string): void {
    this.success(`${entityName} added`, message ?? `${entityName} was added successfully.`);
  }

  updated(entityName: string, message?: string): void {
    this.success(`${entityName} updated`, message ?? `${entityName} was updated successfully.`);
  }

  deleted(entityName: string, message?: string): void {
    this.success(`${entityName} deleted`, message ?? `${entityName} was removed successfully.`);
  }

  exported(entityName: string, message?: string): void {
    this.success(`${entityName} exported`, message ?? `${entityName} export completed successfully.`);
  }

  dismiss(): void {
    this.toast.set(null);
  }
}
